import React, { useRef, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';

import MyYusoServicesIcon from '../svg/MyYusoServicesIcon';
import InsideIcon from '../svg/InsideIcon';
import AcessIcon from '../svg/AcessIcon';
import { useTranslation } from 'react-i18next';
import MonitoringIcon from '../svg/MonitoringIcon';
import { RootState } from '../../store/indexRedux';
import { useSelector } from 'react-redux';
import ArrowLongLeftIcon from '../svg/ArrowLongLeftIcon';

interface SideMenuProps {
  isHamburgerOpen: boolean;
  setIsHamburgerOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const SideMenu: React.FC<SideMenuProps> = ({ isHamburgerOpen, setIsHamburgerOpen }) => {
  const hamburgerMenuRef = useRef<HTMLDivElement | null>(null);
  const { t } = useTranslation('Yusoapps');
  const isMobile = useMediaQuery({ maxWidth: 767 });

  const getLandingUrl = (): string => {
    return window.location.hostname === 'localhost' ? 'http://localhost:3001' : import.meta.env.VITE_LANDING;
  };

  const bessServices = useSelector((state: RootState) => state.companyPortfolio.data?.services?.bess);
  const bessExists = (bessServices?.length ?? 0) > 0;

  useEffect(() => {
    const handleClickOutsideHamburger = (event: MouseEvent) => {
      if (hamburgerMenuRef.current && !hamburgerMenuRef.current.contains(event.target as Node)) {
        setIsHamburgerOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutsideHamburger);
    return () => {
      document.removeEventListener('mousedown', handleClickOutsideHamburger);
    };
  }, [setIsHamburgerOpen]);

  return (
    <div
      ref={hamburgerMenuRef}
      className={`fixed dark:bg-primary-900 top-0 left-0 h-full bg-base-100 z-50 w-[313px]  shadow-2xl transform ${
        isHamburgerOpen ? 'translate-x-0' : '-translate-x-full'
      } transition-transform duration-300 ease-in-out`}
    >
      {isMobile ? (
        <div className='flex flex-col h-full'>
          <div className='flex my-5 items-center gap-2  px-[1.75rem]  text-primary-600 font-semibold hover:bg-gray-50 dark:text-white '>
            <MyYusoServicesIcon width={35} height={24} color='#91B1BE' />
            <h2>{t('myYusoServices')}</h2>
          </div>
          {location.pathname !== '/' && location.pathname !== '/add-company' && (
            <ul className='text-base space-y-4 p-5 '>
              <li>
                <NavLink
                  to='/overview'
                  className='flex items-center gap-4 p-2 text-primary-400 hover:text-primary-600 hover:bg-gray-50 dark:text-white'
                  onClick={() => setIsHamburgerOpen(false)}
                >
                  {t('start')}
                </NavLink>
              </li>
              <li>
                <NavLink
                  to='/services'
                  className='flex items-center gap-4 p-2 text-primary-400 hover:text-primary-600 hover:bg-gray-50 dark:text-white'
                  onClick={() => setIsHamburgerOpen(false)}
                >
                  {t('portfolio')}
                </NavLink>
              </li>
              <li>
                <NavLink
                  to='/documents'
                  className='flex items-center gap-4 p-2 text-primary-400 hover:text-primary-600 hover:bg-gray-50 dark:text-white'
                  onClick={() => setIsHamburgerOpen(false)}
                >
                  {t('documents')}
                </NavLink>
              </li>
              {/* Mobile counterpart of the desktop "Manage" link, which is
                  hidden behind !isMobile in MainNavigation — without this entry
                  End Of Contract was unreachable on a phone except by typing
                  the URL. Points at /management, NOT straight at
                  /management/end-contract, so ManagementPage stays the single
                  place that decides which cards a role may see (Fluvius and
                  BESS re-sign remain behind isAdmin there, and behind
                  AdminProtectedRoute on their routes). Intentionally NOT
                  role-gated: every authenticated user may open the page. */}
              <li>
                <NavLink
                  to='/management'
                  className='flex items-center gap-4 p-2 text-primary-400 hover:text-primary-600 hover:bg-gray-50 dark:text-white'
                  onClick={() => setIsHamburgerOpen(false)}
                >
                  {t('management')}
                </NavLink>
              </li>
              {/* <li>
              <NavLink
                to={getLandingUrl()}
                className='flex items-center gap-4 p-2 text-primary-600 hover:text-primary-800 dark:text-white'
                onClick={() => setIsHamburgerOpen(false)}
              >
                Mandates
              </NavLink>
            </li> */}
            </ul>
          )}
          <div className='flex mt-auto sticky bottom-0' style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
            <NavLink
              to={getLandingUrl()}
              className='flex-1 flex items-center gap-3 text-left pl-3 py-5 bg-primary-600 hover:bg-primary-700 text-white'
            >
              <ArrowLongLeftIcon /> <span> {t('backToConsole')}</span>
            </NavLink>
          </div>
        </div>
      ) : (
        <>
          <ul className='text-base space-y-4 p-5 font-semibold'>
            <li>
              <NavLink
                to={getLandingUrl()}
                className='flex items-center gap-4 p-2 text-primary-600 hover:text-primary-800 dark:text-white'
                onClick={() => setIsHamburgerOpen(false)}
              >
                <MyYusoServicesIcon width={28} height={19} color='#B4CAD3' />
                {t('consoleTitle')}
              </NavLink>
            </li>
            {bessExists && (
              <li>
                <NavLink
                  to={import.meta.env.VITE_INSIDE as string}
                  className='flex items-center gap-4 p-2 text-primary-600 hover:text-primary-800 dark:text-white'
                  onClick={() => setIsHamburgerOpen(false)}
                >
                  <InsideIcon width={28} height={28} color='#B4CAD3' />
                  {t('insideTitle')}
                </NavLink>
              </li>
            )}
            <li>
              <NavLink
                to={import.meta.env.VITE_ACCESS as string}
                className='flex items-center gap-4 p-2 text-primary-600 hover:text-primary-800 dark:text-white'
                onClick={() => setIsHamburgerOpen(false)}
              >
                <AcessIcon width={28} height={28} color='#B4CAD3' />
                {t('yusoAccess')}
              </NavLink>
            </li>
            <li>
              <NavLink
                to={import.meta.env.VITE_YUSO_LEGACY as string}
                className='flex items-center gap-4 p-2 text-primary-600 hover:text-primary-800 dark:text-white'
                onClick={() => setIsHamburgerOpen(false)}
              >
                <MonitoringIcon width={28} height={28} color='#B4CAD3' />
                {t('myYusoLegacy')}
              </NavLink>
            </li>
          </ul>
        </>
      )}
    </div>
  );
};

export default SideMenu;
